import { IFreeConsultancyRequest } from '../utils/entities/interface';
import { Pagination } from './booking';
import { get, post } from './https';

interface ConsultancyRequestsResponse {
  status: boolean;
  data: IFreeConsultancyRequest[];
  count: number;
  pagination: Pagination;
}

const GETConsultancyRequests = async ({
  page = 1,
  limit = 25,
  searchQuery = '',
  sortField = '',
  sortOrder = 'DESC',
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<ConsultancyRequestsResponse> => {
  // Initialize the base URL
  let url = `/api/v1/admin/consultancy-list?limit=${limit}&page=${page}`;

  // Conditionally add search parameter if provided
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  // Conditionally add sortField and sortOrder if they are provided
  if (sortField && sortField !== 'name') {
    // assuming 'name' is the default field, you can change it if needed
    url += `&sortField=${sortField}`;
  }

  if (sortOrder && sortOrder !== 'ASC') {
    // assuming 'ASC' is the default sort order
    url += `&sortOrder=${sortOrder}`;
  }

  // Fetch the response
  const response = await get(url); // Use the previously defined `get` function

  if (response.status == 200) {
    return response.data; // If status is true, return the user data
  } else {
    throw new Error('Failed to fetch users');
  }
};

// Admin: single consultancy request (endpoint replies 201 on success)
const GETConsultancyRequestById = async (consultancyId: string) => {
  if (!consultancyId) {
    throw new Error('Consultancy id is required');
  }
  const response = await get(`/api/v1/admin/consultancy/${consultancyId}`);
  if (
    (response?.status === 200 || response?.status === 201) &&
    response?.data?.status
  ) {
    return response.data.data;
  }
  throw new Error(
    response?.data?.message || 'Failed to fetch consultancy request',
  );
};

// Admin: update consultancy status (e.g. reject a request)
const UpdateConsultancyStatus = async (
  consultancyId: string,
  status: 'BOOKED' | 'COMPLETE' | 'CANCELLED' | 'CLOSED',
  reason?: string,
) => {
  if (!consultancyId) {
    throw new Error('Consultancy id is required');
  }
  const response = await post('/api/v1/admin/consultancy/status/manage', {
    consultancyId,
    status,
    ...(reason ? { reason } : {}),
  });
  if (response?.status === 200 && response?.data?.status) {
    return response.data;
  }
  throw new Error(
    response?.data?.message || 'Failed to update consultancy status',
  );
};

// Admin: create a free consultancy request on a customer's behalf. `photos`
// is an array of already-hosted URLs — the image itself is uploaded
// straight from the browser to S3 via a presigned URL (see
// components/atoms/UploadImage.tsx) before this is ever called, so this is
// a plain JSON call, same as every other field.
const AdminCreateConsultancy = async (payload: Record<string, any>) => {
  const response = await post('/api/v1/admin/consultancy/create-edit', {
    consultancyId: '',
    ...payload,
  });
  if (response.status === 200 || response.status === 201) {
    return response.data;
  }
  throw new Error(response?.data?.message || 'Failed to create request');
};

export {
  GETConsultancyRequests,
  GETConsultancyRequestById,
  UpdateConsultancyStatus,
  AdminCreateConsultancy,
};
